import { useMemo, useRef, useState } from "react"
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  CheckCircle2,
  ChevronRight,
  Info,
  Pencil,
  Plus,
  RotateCcw,
  Save,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import {
  usinageInputBags,
  usinageInputKg,
} from "@/features/mill-floor/lib/load-math"
import { AddLoadsDialog } from "@/features/milling/components/add-loads-dialog"
import { ConfirmCouplageDialog } from "@/features/milling/components/confirm-couplage-dialog"
import { CouplagePlanTable } from "@/features/milling/components/couplage-plan-table"
import { CouplageReportPanel } from "@/features/milling/components/couplage-report-panel"
import { ReportDropdown } from "@/features/milling/components/report-dropdown"
import { downloadCouplageRapportPdf } from "@/features/milling/lib/couplage-rapport-pdf"
import { downloadCouplageRapportXlsx } from "@/features/milling/lib/couplage-rapport-xlsx"
import { downloadUsinageRapportPdf } from "@/features/milling/lib/usinage-rapport-pdf"
import { downloadUsinageRapportXlsx } from "@/features/milling/lib/usinage-rapport-xlsx"
import { buildRapport } from "@/features/milling/lib/rapport"
import { FabricationList } from "@/features/milling/components/fabrication-list"
import { FreintePanel } from "@/features/milling/components/freinte-panel"
import { WasteRequiredDialog } from "@/features/milling/components/waste-required-dialog"
import {
  type StepKey,
  UsinageStepper,
} from "@/features/milling/components/usinage-stepper"
import {
  type CouplagePlanColumn,
  type CouplageMatrix,
  type HelperIssue,
  collectCouplageIssues,
  decodeSourceKey,
  sourceKey,
  validateCouplage,
} from "@/features/milling/lib/couplage-validation"
import { LOT_TARGET_BAGS } from "@/features/milling/lib/quality"
import type {
  CouplingLoadView,
  UsinageAssignmentRow,
  UsinageQdlAssignmentRow,
  UsinageDetail,
} from "@/features/milling/api/types"
import { useUsinage } from "@/features/milling/hooks/use-milling"
import {
  usePatchUsinageLoads,
  useRewindCouplage,
  useSaveCouplageDraft,
  useSubmitCouplage,
} from "@/features/milling/hooks/use-milling-mutations"
import { formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

// Flatten an embedded ExporterUsinageLoads row into the table's
// view-shape. LOAD-only — the QDL side has its own toQdlView helper.
function toLoadView(a: UsinageAssignmentRow): CouplingLoadView {
  return {
    loadId: a.loadId,
    sourceKind: "LOAD",
    loadNumber: a.load?.loadNumber ?? null,
    plateNumber: a.load?.plateNumber ?? null,
    deliverySlip: a.load?.DeliveryForms?.billOfLadingNumber ?? null,
    cooperativeName: a.load?.Orders?.groupId?.name ?? null,
    customerName: a.load?.Orders?.exporterOrderId?.clientName ?? null,
    certifications: a.load?.Orders?.certificates ?? null,
    arrivedAt: null,
    bagsAssigned: a.bagsAssigned,
    avgBagKg: a.avgBagKg ?? null,
    // Prefer the snapshot avgBagKg from the assignment row — it was
    // computed at schedule time against the post-sondage bag count,
    // so multiplying it back by bagsAssigned yields the accepted kg
    // that's actually in the warehouse for this assignment.
    netKg:
      a.avgBagKg != null
        ? a.avgBagKg * a.bagsAssigned
        : null,
    grade: a.load?.grade ?? null,
  }
}

// Flatten an embedded ExporterUsinageQDLs row into the table's
// view-shape. QDLs surface a `lotNumber` identity instead of a
// delivery slip / plate, and carry no cooperative / certifications /
// customer.
function toQdlView(a: UsinageQdlAssignmentRow): CouplingLoadView {
  const q = a.queueLot
  return {
    loadId: a.queueLotId,
    sourceKind: "QUEUE_LOT",
    loadNumber: q?.lotNumber ?? null,
    plateNumber: null,
    deliverySlip: null,
    cooperativeName: null,
    customerName: null,
    certifications: null,
    arrivedAt: null,
    bagsAssigned: a.bagsAssigned,
    avgBagKg: a.avgBagKg ?? q?.avgBagKg ?? null,
    netKg:
      a.avgBagKg != null
        ? a.avgBagKg * a.bagsAssigned
        : q?.netKg != null && q?.bags
          ? (q.netKg / q.bags) * a.bagsAssigned
          : null,
    grade: null,
  }
}

function newPlanColumn(existingLabels: string[]): CouplagePlanColumn {
  let n = existingLabels.length + 1
  while (existingLabels.includes(`LOT PLANNED ${n}`)) n += 1
  return {
    tempId: `COL-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    label: `LOT PLANNED ${n}`,
    plannedBags: 0,
  }
}

export function UsinageDetailPage() {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const { usinageId } = useParams()
  const { data: usinage, isLoading, isError } = useUsinage(usinageId)

  if (isLoading) return <LoadingState />
  if (isError || !usinage) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" onClick={() => navigate(paths.millFloor)}>
          <ArrowLeft className="size-4" />
          {t("detail.back")}
        </Button>
        <PageHeader title={t("detail.notFound")} />
      </div>
    )
  }

  // State-machine dispatch. SCHEDULED/RUNNING/PENDING_COUPLING share
  // the editable planning view; COUPLAGE_PLANNED/FABRICATION share
  // the fabrication queue view (the plan is read-only at that point);
  // COMPLETED is the read-only report.
  switch (usinage.status) {
    case "COMPLETED":
      return <CompletedView usinage={usinage} />
    case "COUPLAGE_PLANNED":
    case "FABRICATION":
      return <FabricationView usinage={usinage} />
    default:
      return <PlanningView usinageId={usinage.id} usinage={usinage} />
  }
}

// PlanningView is Step 1: the operator builds a Lot Planned matrix
// from the chargements attached to this usinage. No weights are
// collected here. On submit the plan is persisted as JSON on the
// usinage row and the run advances to COUPLAGE_PLANNED.
function PlanningView({
  usinageId,
  usinage,
}: {
  usinageId: string | number
  usinage: UsinageDetail
}) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const submit = useSubmitCouplage(usinageId)
  const saveDraft = useSaveCouplageDraft(usinageId)
  const patchLoads = usePatchUsinageLoads(usinageId)

  // Both source streams flow into the same `loads` array as
  // CouplingLoadView rows — sourceKind discriminates downstream. The
  // matrix is keyed by `<sourceKind>:<id>` so LOAD#7 and QDL#7 stay
  // distinct.
  const loads = useMemo<CouplingLoadView[]>(
    () => [
      ...(usinage.assignments ?? []).map(toLoadView),
      ...(usinage.qdlAssignments ?? []).map(toQdlView),
    ],
    [usinage.assignments, usinage.qdlAssignments],
  )
  const inputBags = useMemo(() => usinageInputBags(usinage), [usinage])
  const inputKg = useMemo(() => usinageInputKg(usinage), [usinage])
  const inputMT = kgToMt(inputKg)

  // Seed the editor from any previously-saved plan (draft or
  // rewound-from-confirmed). Each planned entry's UUID is kept as the
  // tempId so the backend can reuse the existing id on re-submit.
  // Matrix is keyed by sourceKey so LOAD#7 and QDL#7 stay distinct.
  const { initialColumns, initialMatrix } = useMemo(() => {
    const cols: CouplagePlanColumn[] = []
    const mat: CouplageMatrix = {}
    for (const p of usinage.lotsPlanned ?? []) {
      cols.push({
        tempId: p.id,
        label: p.label,
        plannedBags: p.plannedBags,
      })
      for (const c of p.cells ?? []) {
        const kind = c.sourceKind ?? "LOAD"
        const key = sourceKey(kind, c.loadId)
        if (!mat[key]) mat[key] = {}
        mat[key][p.id] = c.bags
      }
    }
    return { initialColumns: cols, initialMatrix: mat }
  }, [usinage.lotsPlanned])

  const [columns, setColumns] = useState<CouplagePlanColumn[]>(initialColumns)
  const [matrix, setMatrix] = useState<CouplageMatrix>(initialMatrix)
  const [addLoadsOpen, setAddLoadsOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const helperRef = useRef<HTMLDivElement | null>(null)

  const totals = useMemo(() => {
    const totalBags = columns.reduce(
      (a, c) => a + (Number(c.plannedBags) || 0),
      0,
    )
    const totalEstKg = columns.reduce((a, c) => {
      const est = loads.reduce((acc, l) => {
        const bags =
          Number(matrix[sourceKey(l.sourceKind, l.loadId)]?.[c.tempId]) || 0
        return acc + bags * (l.avgBagKg ?? 0)
      }, 0)
      return a + est
    }, 0)
    const matrixBags = loads.reduce(
      (a, l) =>
        a +
        Object.values(
          matrix[sourceKey(l.sourceKind, l.loadId)] ?? {},
        ).reduce((b, v) => b + (Number(v) || 0), 0),
      0,
    )
    return {
      totalBags,
      totalEstMT: kgToMt(totalEstKg),
      matrixBags,
      remainingBags: Math.max(0, inputBags - totalBags),
    }
  }, [columns, loads, matrix, inputBags])

  const errors = useMemo(
    () => validateCouplage({ columns, loads, matrix }, t as never),
    [columns, loads, matrix, t],
  )
  const issues = useMemo(
    () => collectCouplageIssues({ columns, loads, matrix }),
    [columns, loads, matrix],
  )
  const canSubmit = errors.length === 0 && columns.length > 0

  const updateColumn = (
    tempId: string,
    patch: Partial<CouplagePlanColumn>,
  ) => {
    setColumns((prev) =>
      prev.map((c) => (c.tempId === tempId ? { ...c, ...patch } : c)),
    )
  }

  // Add a fresh empty column. The Lot Planned's bag count IS whatever
  // the operator routes into it via the matrix — there is no separately
  // entered target. Operators may freely route > 385 bags per lot to
  // absorb milling losses.
  const addColumn = () => {
    const base = newPlanColumn(columns.map((c) => c.label))
    setColumns((prev) => [...prev, base])
  }

  const removeColumn = (tempId: string) => {
    setColumns((prev) => prev.filter((c) => c.tempId !== tempId))
    setMatrix((prev) => {
      const next: CouplageMatrix = {}
      for (const [key, row] of Object.entries(prev)) {
        const rest: Record<string, number> = {}
        for (const [k, v] of Object.entries(row)) {
          if (k !== tempId) rest[k] = v
        }
        if (Object.keys(rest).length > 0) next[key] = rest
      }
      return next
    })
  }

  // Routes `raw` bags from a source (LOAD or QDL) into the column
  // identified by `tempId`. Side-effect: refreshes the affected
  // column's mirror `plannedBags` field so downstream reads (totals,
  // helper bar, submit payload) stay in sync without a separate
  // effect.
  const setCell = (srcKey: string, tempId: string, raw: number) => {
    const n = Math.max(0, Math.floor(Number(raw) || 0))
    setMatrix((prev) => {
      const next: CouplageMatrix = { ...prev }
      const row = { ...(next[srcKey] ?? {}) }
      if (n === 0) delete row[tempId]
      else row[tempId] = n
      if (Object.keys(row).length === 0) delete next[srcKey]
      else next[srcKey] = row
      // Recompute this column's total from the freshly-mutated matrix
      // and patch the column's `plannedBags` mirror.
      const colSum = loads.reduce((a, l) => {
        const k = sourceKey(l.sourceKind, l.loadId)
        return a + (Number(k === srcKey ? row[tempId] : next[k]?.[tempId]) || 0)
      }, 0)
      setColumns((prevCols) =>
        prevCols.map((c) =>
          c.tempId === tempId ? { ...c, plannedBags: colSum } : c,
        ),
      )
      return next
    })
  }

  const clearMatrix = () => setMatrix({})

  const removeLoad = (loadId: number, sourceKind: "LOAD" | "QUEUE_LOT") => {
    if (loads.length <= 1) return
    const targetKey = sourceKey(sourceKind, loadId)
    setMatrix((prev) => {
      const next: CouplageMatrix = {}
      for (const [k, v] of Object.entries(prev)) {
        if (k !== targetKey) next[k] = v
      }
      return next
    })
    if (sourceKind === "QUEUE_LOT") {
      patchLoads.mutate({ removeQueueLots: [loadId] })
    } else {
      patchLoads.mutate({ remove: [loadId] })
    }
  }

  const onSubmitBlocked = () => {
    helperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    toast.error(
      t("detail.helper.blockedToast", { count: errors.length || 1 }),
    )
  }

  // Shared payload builder used by both Save draft and Submit. Even
  // empty matrix is acceptable for drafts — the backend's draft path
  // skips the "every column has cells" rule.
  const buildPayload = () => {
    const lots = columns.map((c) => ({
      tempId: c.tempId,
      label: c.label.trim(),
      plannedBags: Number(c.plannedBags) || 0,
    }))
    // Matrix is keyed by "<sourceKind>:<id>"; decode each cell back
    // into the wire shape the backend expects (loadId + sourceKind).
    // Default sourceKind is LOAD so older payloads keep working.
    const assignments = Object.entries(matrix).flatMap(([key, row]) => {
      const decoded = decodeSourceKey(key)
      if (!decoded) return []
      return Object.entries(row).map(([tempId, bags]) => {
        const cell: {
          tempId: string
          loadId: number
          bags: number
          sourceKind?: "QUEUE_LOT"
        } = {
          tempId,
          loadId: decoded.id,
          bags: Number(bags) || 0,
        }
        if (decoded.kind === "QUEUE_LOT") {
          cell.sourceKind = "QUEUE_LOT"
        }
        return cell
      })
    })
    return {
      submittedBy: usinage.operator,
      lots,
      assignments,
    }
  }

  const onSaveDraft = () => {
    saveDraft.mutate(buildPayload())
  }

  const onSubmit = () => {
    if (!canSubmit) {
      onSubmitBlocked()
      return
    }
    setConfirmOpen(true)
  }

  const onConfirmSubmit = () => {
    submit.mutate(buildPayload(), {
      onSuccess: () => setConfirmOpen(false),
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(paths.millFloor)}
      >
        <ArrowLeft className="size-4" />
        {t("detail.back")}
      </Button>

      <PageHeader
        eyebrow={t("detail.operator", { operator: usinage.operator })}
        title={usinage.usinageNumber}
        description={t("detail.subtitle", {
          range: dateRange(usinage.startDate, usinage.endDate),
          loads: loads.length,
          loadsWord: t(
            loads.length === 1
              ? "detail.loadSingular"
              : "detail.loadPlural",
          ),
          mt: formatNumber(inputMT, 3),
        })}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddLoadsOpen(true)}
            >
              <Plus className="size-4" />
              {t("detail.report.addLoad")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onSaveDraft}
              disabled={saveDraft.isPending}
            >
              <Save className="size-4" />
              {saveDraft.isPending
                ? t("detail.report.savingDraft")
                : t("detail.report.saveDraft")}
            </Button>
            <Button
              onClick={onSubmit}
              disabled={submit.isPending}
              variant={canSubmit ? "default" : "secondary"}
              aria-disabled={!canSubmit || undefined}
              title={!canSubmit ? errors[0] : undefined}
            >
              {canSubmit ? (
                <CheckCircle2 className="size-4" />
              ) : (
                <AlertTriangle className="size-4" />
              )}
              {t("detail.report.submitCouplage")}
            </Button>
          </div>
        }
      />

      <UsinageStepper status={usinage.status} />

      <PlanningKpis
        inputBags={inputBags}
        inputMT={inputMT}
        matrixBags={totals.matrixBags}
        plannedBags={totals.totalBags}
        estimatedMT={totals.totalEstMT}
        plannedLots={columns.length}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">
          {t("detail.report.eyebrowPlanning")}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearMatrix}
          disabled={Object.keys(matrix).length === 0}
        >
          <RotateCcw className="size-4" />
          {t("detail.report.clearMatrix")}
        </Button>
      </div>

      {columns.length > 0 ? (
        <HelperBar
          inputBags={inputBags}
          columns={columns}
          loads={loads}
          matrix={matrix}
          issues={issues}
          canAddColumn={totals.remainingBags > 0}
          onAddColumn={addColumn}
          balanced={canSubmit && totals.totalBags > 0}
          scrollRef={helperRef}
        />
      ) : null}

      <CouplagePlanTable
        usinage={usinage}
        loads={loads}
        columns={columns}
        matrix={matrix}
        inputBags={inputBags}
        onUpdateColumn={updateColumn}
        onAddColumn={addColumn}
        onRemoveColumn={removeColumn}
        onSetCell={setCell}
        onRemoveLoad={removeLoad}
        canAddColumn={totals.remainingBags > 0}
        canRemoveLoad={loads.length > 1}
      />

      <AddLoadsDialog
        open={addLoadsOpen}
        onOpenChange={setAddLoadsOpen}
        usinageId={usinageId}
        existingLoadIds={loads.map((l) => l.loadId)}
      />

      <ConfirmCouplageDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        loads={loads}
        columns={columns}
        matrix={matrix}
        onConfirm={onConfirmSubmit}
        isPending={submit.isPending}
      />
    </div>
  )
}

// FabricationView is Step 2: the couplage plan is committed; the
// operator works through the planned entries one at a time, recording
// actual bag count + net weight per Lot Planned. The plan matrix
// renders read-only so the operator can cross-reference.
function FabricationView({ usinage }: { usinage: UsinageDetail }) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const rewind = useRewindCouplage(usinage.id)
  const loads = useMemo<CouplingLoadView[]>(
    () => [
      ...(usinage.assignments ?? []).map(toLoadView),
      ...(usinage.qdlAssignments ?? []).map(toQdlView),
    ],
    [usinage.assignments, usinage.qdlAssignments],
  )
  const inputKg = useMemo(() => usinageInputKg(usinage), [usinage])
  const inputMT = kgToMt(inputKg)

  // "Edit plan" rewinds COUPLAGE_PLANNED → RUNNING so the operator
  // can tweak the matrix. Once any Lot Planned has been fabricated
  // the usinage is in FABRICATION and rewinding would orphan the
  // produced lots, so the button hides itself.
  const canRewind = usinage.status === "COUPLAGE_PLANNED"

  return (
    <div className="flex flex-col gap-6">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(paths.millFloor)}
      >
        <ArrowLeft className="size-4" />
        {t("detail.back")}
      </Button>

      <PageHeader
        eyebrow={t("detail.operator", { operator: usinage.operator })}
        title={usinage.usinageNumber}
        description={t("detail.subtitle", {
          range: dateRange(usinage.startDate, usinage.endDate),
          loads: loads.length,
          loadsWord: t(
            loads.length === 1
              ? "detail.loadSingular"
              : "detail.loadPlural",
          ),
          mt: formatNumber(inputMT, 3),
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/* Only the couplage rapport is available during
                fabrication — the synthèse (entrée + fabrication +
                freinte) needs the run to be COMPLETED before it
                makes sense. */}
            <ReportDropdown
              label={t("couplageReport.title")}
              onDownloadPdf={() =>
                downloadCouplageRapportPdf(usinage, loads, t as never)
              }
              onDownloadXlsx={() =>
                downloadCouplageRapportXlsx(usinage, loads, t as never)
              }
            />
            {canRewind ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => rewind.mutate()}
                disabled={rewind.isPending}
              >
                <Pencil className="size-4" />
                {t("detail.report.editPlan")}
              </Button>
            ) : null}
          </div>
        }
      />

      <UsinageStepper status={usinage.status} />

      <FabricationList usinage={usinage} loads={loads} />

      {/* Couplage report — chargement × lot matrix in the cooperative
          paper format. Always open; both PDF and Excel exporters are
          accessible from its header. Replaces the earlier
          read-only CouplagePlanTable collapsible. */}
      <CouplageReportPanel usinage={usinage} loads={loads} />
    </div>
  )
}

// CompletedView renders the final report: KPI strip + the produced
// lot matrix. Largely unchanged from the previous implementation —
// the only material difference is that QUEUE lots no longer carry a
// matrix breakdown (they're virtual loads now, see redesign §7).
function CompletedView({ usinage }: { usinage: UsinageDetail }) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const lots = usinage.lots ?? []
  // Source count = LOAD assignments + QDL assignments.
  const sourceCount =
    (usinage.assignments ?? []).length +
    (usinage.qdlAssignments ?? []).length

  // The stepper drives which past step the operator is browsing.
  // Default to "complete" — the final report. Clicking "plan" or
  // "fabricate" on the stepper switches the body to a read-only
  // version of that step's view.
  const [viewedStep, setViewedStep] = useState<StepKey>("complete")

  // Flattened source view — same shape PlanningView / FabricationView
  // build so CouplageReportPanel + the report downloads can resolve
  // cooperative names / QDL labels.
  const loads = useMemo<CouplingLoadView[]>(
    () => [
      ...(usinage.assignments ?? []).map(toLoadView),
      ...(usinage.qdlAssignments ?? []).map(toQdlView),
    ],
    [usinage.assignments, usinage.qdlAssignments],
  )

  // Re-seed the couplage matrix from `lotsPlanned` so the read-only
  // planning view shows exactly what was committed at submit time.
  // Same shape PlanningView builds, but the values never flow back
  // into a mutation hook.
  const { plannedColumns, plannedMatrix } = useMemo(() => {
    const cols: CouplagePlanColumn[] = []
    const mat: CouplageMatrix = {}
    for (const p of usinage.lotsPlanned ?? []) {
      cols.push({
        tempId: p.id,
        label: p.label,
        plannedBags: p.plannedBags,
      })
      for (const c of p.cells ?? []) {
        const kind = c.sourceKind ?? "LOAD"
        const key = sourceKey(kind, c.loadId)
        if (!mat[key]) mat[key] = {}
        mat[key][p.id] = c.bags
      }
    }
    return { plannedColumns: cols, plannedMatrix: mat }
  }, [usinage.lotsPlanned])

  const inputBags = useMemo(() => usinageInputBags(usinage), [usinage])
  const inputKg = useMemo(() => usinageInputKg(usinage), [usinage])
  const inputMT = kgToMt(inputKg)
  const totalBags = lots.reduce((a, l) => a + (l.bags || 0), 0)
  const fullLots = lots.filter((l) => l.kind === "FULL").length
  const queueLots = lots.filter((l) => l.kind === "QUEUE").length
  // KPI loss% reads the new Freinte summary (engaged − obtained,
  // normalised by engaged) so the KPI strip matches the table below.
  const freinteSummary = useMemo(
    () => buildRapport(usinage, loads).freinte.summary,
    [usinage, loads],
  )

  // No-op handlers for the read-only CouplagePlanTable; readOnly
  // suppresses all the interactive UI but the prop types still
  // require the callbacks.
  const noop = () => {}

  // Rapport download gating — the Freinte rapport's Ecart hinges on
  // the run-wide déchet (waste) figure, but operators rarely scroll
  // down to the Freinte summary editor before clicking "Download".
  // We intercept the click and, when wasteKg is still unset, queue
  // the download behind a modal that captures it. `pendingDownload`
  // is the deferred exporter; the dialog calls it onProceed.
  const [pendingDownload, setPendingDownload] = useState<
    null | (() => void)
  >(null)
  const wasteUnset = (usinage.wasteKg ?? 0) === 0

  const runReportDownload = (action: () => void) => {
    if (wasteUnset) {
      // Wrap in an object so React's setState doesn't try to call
      // a raw function as the updater.
      setPendingDownload(() => action)
      return
    }
    action()
  }

  return (
    <div className="flex flex-col gap-6">
      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => navigate(paths.millFloor)}
      >
        <ArrowLeft className="size-4" />
        {t("detail.back")}
      </Button>

      <PageHeader
        eyebrow={t("detail.operator", { operator: usinage.operator })}
        title={usinage.usinageNumber}
        description={t("detail.subtitle", {
          range: dateRange(usinage.startDate, usinage.endDate),
          loads: sourceCount,
          loadsWord: t(
            sourceCount === 1
              ? "detail.loadSingular"
              : "detail.loadPlural",
          ),
          mt: formatNumber(inputMT, 3),
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ReportDropdown
              label={t("couplageReport.title")}
              onDownloadPdf={() =>
                downloadCouplageRapportPdf(usinage, loads, t as never)
              }
              onDownloadXlsx={() =>
                downloadCouplageRapportXlsx(usinage, loads, t as never)
              }
            />
            {/* Usinage rapport (synthèse) is only meaningful once the
                run is COMPLETED — that's the only branch that
                reaches this view, so it's safe to show here. The
                déchet figure feeds the rapport's Ecart line, so we
                route the click through `runReportDownload` which
                prompts the operator for déchet when it hasn't been
                set yet (see WasteRequiredDialog below). */}
            <ReportDropdown
              label={t("rapport.title")}
              onDownloadPdf={() =>
                runReportDownload(() =>
                  downloadUsinageRapportPdf(usinage, loads, t as never),
                )
              }
              onDownloadXlsx={() =>
                runReportDownload(() =>
                  downloadUsinageRapportXlsx(usinage, loads, t as never),
                )
              }
            />
            <Button size="sm" onClick={() => navigate(paths.lots)}>
              {t("detail.viewLots")}
            </Button>
          </div>
        }
      />

      <UsinageStepper
        status={usinage.status}
        activeStep={viewedStep}
        onStepChange={setViewedStep}
      />

      {viewedStep === "plan" ? (
        // Read-only couplage plan matrix. No HelperBar, no Add Lot
        // button — just the committed plan as it stood at submit.
        <>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="size-3.5" />
            {t("detail.report.reviewModeHint")}
          </div>
          <CouplagePlanTable
            usinage={usinage}
            loads={loads}
            columns={plannedColumns}
            matrix={plannedMatrix}
            inputBags={inputBags}
            onUpdateColumn={noop}
            onAddColumn={noop}
            onRemoveColumn={noop}
            onSetCell={noop}
            canAddColumn={false}
            canRemoveLoad={false}
            readOnly
          />
        </>
      ) : viewedStep === "fabricate" ? (
        // Read-only fabrication summary: the produced-lots list (all
        // entries will be FABRICATED_* on a COMPLETED run, so the
        // "Fabriquer" button never shows) + the couplage report.
        <>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="size-3.5" />
            {t("detail.report.reviewModeHint")}
          </div>
          <FabricationList usinage={usinage} loads={loads} />
          <CouplageReportPanel usinage={usinage} loads={loads} />
        </>
      ) : (
        // Default: the final report — KPIs + Freinte rapport +
        // traceability matrix.
        <>
          <CompletedKpis
            engagedKg={freinteSummary.totalPlannedKg}
            obtainedKg={freinteSummary.totalActualKg}
            wasteKg={freinteSummary.wasteKg}
            ecartKg={freinteSummary.ecartKg}
            freintePct={freinteSummary.freintePct}
            fullLots={fullLots}
            queueLots={queueLots}
            totalBags={totalBags}
            endDate={usinage.endDate ?? null}
          />

          {/* Traceability matrix — the on-screen forensic view on
              the COMPLETED step. Downloads (rapport + couplage
              matrix) live in the PageHeader actions above. */}
          <Card className="flex flex-col gap-0 overflow-hidden p-0">
            <div className="border-b bg-muted/40 px-4 py-3">
              <h3 className="text-sm font-semibold tracking-tight">
                {t("detail.report.completedMatrixTitle")}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t("detail.report.completedMatrixHint")}
              </p>
            </div>
            <CompletedMatrixTable usinage={usinage} />
          </Card>

          {/* Freinte rapport — per-planned-lot reconciliation.
              Rendered below the traceability matrix so the audit
              trail (which lot came from which source) reads first,
              and the engaged-vs-obtained reconciliation closes the
              page. */}
          <FreintePanel usinage={usinage} loads={loads} />
        </>
      )}

      {/* Mount the prompt outside the stepper branches so it stays
          available regardless of which sub-view the operator is
          browsing. The dialog is open whenever a download is
          queued; running the deferred exporter is handled inside
          its onProceed. */}
      <WasteRequiredDialog
        open={pendingDownload !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDownload(null)
        }}
        usinageId={usinage.id}
        onProceed={() => {
          const action = pendingDownload
          setPendingDownload(null)
          action?.()
        }}
      />
    </div>
  )
}

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-3.5 align-middle"

// Read-only matrix viewer for COMPLETED usinages. Pivots
// `lot.lotLoads[]` into a load × lot grid: each input load is one row,
// each produced FULL lot one column. QUEUE lots show as columns
// without the matrix (their bags are now virtual loads of their own).
function CompletedMatrixTable({ usinage }: { usinage: UsinageDetail }) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  // Memoise the fallback so `useMemo` below has a stable dependency
  // identity even when the backend omits `lots` / `assignments`.
  const lots = useMemo(() => usinage.lots ?? [], [usinage.lots])

  // Unified row list combining both source streams. Each row carries
  // a sourceKind so the body renders cooperative / lot-number /
  // slip-or-plate from the appropriate branch.
  type CompletedRow = {
    key: string
    sourceKind: "LOAD" | "QUEUE_LOT"
    id: number
    bagsAssigned: number
    coopName: string
    slip: string
  }
  const rows = useMemo<CompletedRow[]>(() => {
    const out: CompletedRow[] = []
    for (const a of usinage.assignments ?? []) {
      out.push({
        key: sourceKey("LOAD", a.loadId),
        sourceKind: "LOAD",
        id: a.loadId,
        bagsAssigned: a.bagsAssigned ?? 0,
        coopName: a.load?.Orders?.groupId?.name ?? "—",
        slip:
          a.load?.DeliveryForms?.billOfLadingNumber ??
          `#${a.load?.loadNumber ?? a.loadId}`,
      })
    }
    for (const a of usinage.qdlAssignments ?? []) {
      out.push({
        key: sourceKey("QUEUE_LOT", a.queueLotId),
        sourceKind: "QUEUE_LOT",
        id: a.queueLotId,
        bagsAssigned: a.bagsAssigned ?? 0,
        coopName: t("detail.report.row.queueLotBadge"),
        slip: a.queueLot?.lotNumber ?? `#${a.queueLotId}`,
      })
    }
    return out
  }, [usinage.assignments, usinage.qdlAssignments, t])

  // The completed matrix is keyed by sourceKey on the row side so
  // FULL-lot traceability cells from LOAD vs QUEUE_LOT sources never
  // collide. The columns are keyed by `lotId` (numeric) — fine since
  // ExporterLots ids are unique within the run.
  const matrix = useMemo(() => {
    const out: Record<string, Record<number, number>> = {}
    for (const lot of lots) {
      for (const cell of lot.lotLoads ?? []) {
        const kind = cell.sourceKind ?? "LOAD"
        const id = kind === "QUEUE_LOT" ? cell.sourceLotId : cell.loadId
        if (id == null) continue
        const key = sourceKey(kind, id)
        if (!out[key]) out[key] = {}
        out[key][lot.id] = cell.bags
      }
    }
    return out
  }, [lots])

  // Map each fabricated lot back to its source Lot Planned (via
  // `plannedLotRef`) so the column header can surface the planned bag
  // count alongside the actual one — visible audit trail of the
  // milling loss.
  const plannedBagsByLotId = useMemo(() => {
    const out: Record<number, number> = {}
    const byPlannedId: Record<string, number> = {}
    for (const p of usinage.lotsPlanned ?? []) {
      byPlannedId[p.id] = p.plannedBags
    }
    for (const lot of lots) {
      if (lot.plannedLotRef && byPlannedId[lot.plannedLotRef] != null) {
        out[lot.id] = byPlannedId[lot.plannedLotRef]
      }
    }
    return out
  }, [lots, usinage.lotsPlanned])

  if (lots.length === 0) return null

  const totalAssignedBags = rows.reduce((a, x) => a + x.bagsAssigned, 0)

  return (
    <div className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className={headCellClass}>
              {t("detail.report.th.cooperative")}
            </TableHead>
            <TableHead className={headCellClass}>
              {t("detail.report.th.deliverySlip")}
            </TableHead>
            <TableHead className={cn(headCellClass, "text-right")}>
              {t("detail.report.th.bags")}
            </TableHead>
            {lots.map((lot) => (
              <TableHead
                key={lot.id}
                className="border-l bg-muted/40 p-0 align-top"
                style={{ minWidth: 180 }}
              >
                <LotColumnHeader
                  lot={lot}
                  plannedBags={plannedBagsByLotId[lot.id]}
                  onOpen={() => navigate(paths.lotDetail(lot.id))}
                  ariaLabel={t("detail.report.openLotAria", {
                    label: lot.lotNumber,
                  })}
                  bagsShort={t("schedule.bagsShort")}
                  queueTag={t("detail.report.queueTag")}
                />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            return (
              <TableRow key={r.key} className="hover:bg-muted/30">
                <TableCell
                  className={cn(bodyCellClass, "max-w-[220px] font-medium")}
                >
                  <span className="block truncate">{r.coopName}</span>
                </TableCell>
                <TableCell
                  className={cn(bodyCellClass, "font-mono text-xs")}
                >
                  {r.slip}
                </TableCell>
                <TableCell
                  className={cn(bodyCellClass, "text-right tabular-nums")}
                >
                  {r.bagsAssigned.toLocaleString()}
                </TableCell>
                {lots.map((lot) => {
                  const v = matrix[r.key]?.[lot.id] ?? 0
                  return (
                    <TableCell
                      key={lot.id}
                      className={cn(
                        bodyCellClass,
                        "border-l text-right tabular-nums",
                      )}
                    >
                      {v > 0 ? (
                        <span className="font-semibold">
                          {v.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
        <TableFooter>
          <TableRow className="hover:bg-muted/50">
            <TableCell
              colSpan={2}
              className={cn(
                bodyCellClass,
                "text-xs font-medium uppercase tracking-wide text-muted-foreground",
              )}
            >
              {t("detail.report.totalsRow")}
            </TableCell>
            <TableCell
              className={cn(
                bodyCellClass,
                "text-right text-sm font-semibold tabular-nums",
              )}
            >
              {totalAssignedBags.toLocaleString()}
            </TableCell>
            {lots.map((lot) => (
              <TableCell
                key={lot.id}
                className={cn(
                  bodyCellClass,
                  "border-l text-right text-sm font-semibold tabular-nums",
                )}
              >
                {(lot.bags ?? 0).toLocaleString()}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  )
}

function LotColumnHeader({
  lot,
  plannedBags,
  onOpen,
  ariaLabel,
  bagsShort,
  queueTag,
}: {
  lot: UsinageDetail["lots"][number]
  plannedBags?: number
  onOpen: () => void
  ariaLabel: string
  bagsShort: string
  queueTag: string
}) {
  const { t } = useTranslation("milling")
  const isQueue = lot.kind === "QUEUE"
  const mt = kgToMt(lot.netKg ?? 0)
  const actualBags = lot.bags ?? 0
  const showDelta =
    plannedBags != null && plannedBags > 0 && plannedBags !== actualBags
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={ariaLabel}
      className="group flex w-full flex-col items-stretch gap-1.5 px-4 py-3 text-left normal-case tracking-normal transition-colors hover:bg-muted/70"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-foreground">
          {lot.lotNumber}
        </span>
        <ChevronRight className="size-3.5 text-muted-foreground opacity-40 transition-opacity group-hover:opacity-100" />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-xl font-semibold leading-none tabular-nums text-foreground">
          {formatNumber(mt, 2)}
        </span>
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          MT
        </span>
      </div>
      <div className="flex flex-col gap-0.5 text-xs tabular-nums text-muted-foreground">
        <div className="flex items-center gap-2">
          <span>
            {actualBags.toLocaleString()} {bagsShort}
          </span>
          {isQueue ? (
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              {queueTag}
            </span>
          ) : null}
        </div>
        {showDelta ? (
          <span className="text-[10px]">
            {t("detail.report.column.fromPlanned", { bags: plannedBags })}
          </span>
        ) : null}
      </div>
    </button>
  )
}

function dateRange(start: string, end?: string | null): string {
  const s = String(start).slice(0, 10)
  if (!end) return s
  return `${s} → ${String(end).slice(0, 10)}`
}

// Slim KPI strip for the planning step. No weight number is shown yet
// (Step 2 collects it); instead we surface the *estimated* MT derived
// from the matrix as a sanity check.
function PlanningKpis({
  inputBags,
  inputMT,
  matrixBags,
  plannedBags,
  estimatedMT,
  plannedLots,
}: {
  inputBags: number
  inputMT: number
  matrixBags: number
  plannedBags: number
  estimatedMT: number
  plannedLots: number
}) {
  const { t } = useTranslation("milling")
  const matrixPct =
    inputBags > 0 ? Math.round((matrixBags / inputBags) * 100) : 0

  return (
    <Card className="grid grid-cols-2 divide-x divide-y overflow-hidden p-0 sm:grid-cols-4 sm:divide-y-0">
      <KpiTile
        label={t("detail.kpis.input")}
        value={`${formatNumber(inputMT, 2)} MT`}
        sub={t("detail.kpis.inputSub", { bags: formatNumber(inputBags) })}
      />
      <KpiTile
        label={t("detail.kpis.routed")}
        value={`${matrixPct}%`}
        sub={t("detail.kpis.routedSub", {
          current: formatNumber(matrixBags),
          total: formatNumber(inputBags),
        })}
      />
      <KpiTile
        label={t("detail.kpis.plannedLots")}
        value={plannedLots}
        sub={t("detail.kpis.plannedLotsSub", {
          bags: formatNumber(plannedBags),
        })}
      />
      <KpiTile
        label={t("detail.kpis.estimated")}
        value={`${formatNumber(estimatedMT, 2)} MT`}
        sub={t("detail.kpis.estimatedSub")}
      />
    </Card>
  )
}

// Outcome-flavored KPI strip for the COMPLETED view. Loss% reads
// the new Freinte summary (engaged − obtained, normalised by engaged)
// so the strip matches the Freinte rapport rendered below.
function CompletedKpis({
  engagedKg,
  obtainedKg,
  wasteKg,
  ecartKg,
  freintePct,
  fullLots,
  queueLots,
  totalBags,
  endDate,
}: {
  engagedKg: number
  obtainedKg: number
  wasteKg: number
  ecartKg: number
  freintePct: number
  fullLots: number
  queueLots: number
  totalBags: number
  endDate: string | null
}) {
  const { t, i18n } = useTranslation("milling")
  const engagedMT = engagedKg / 1000
  const obtainedMT = obtainedKg / 1000
  const wasteMT = wasteKg / 1000
  const ecartMT = ecartKg / 1000

  const dateFmt = endDate
    ? new Date(endDate).toLocaleDateString(
        i18n.language?.startsWith("fr") ? "fr-FR" : "en-US",
        { year: "numeric", month: "short", day: "numeric" },
      )
    : "—"

  return (
    <Card className="grid grid-cols-2 divide-x divide-y overflow-hidden p-0 sm:grid-cols-4 sm:divide-y-0">
      <KpiTile
        size="md"
        label={t("detail.kpis.output")}
        value={`${formatNumber(obtainedMT, 2)} MT`}
        sub={t("detail.kpis.outputSub", {
          bags: formatNumber(totalBags),
        })}
      />
      <KpiTile
        size="md"
        label={t("detail.kpis.loss")}
        value={`${formatNumber(freintePct, 1)}%`}
        sub={t("detail.kpis.lossSub", {
          input: formatNumber(engagedMT, 2),
          output: formatNumber(obtainedMT, 2),
        })}
        hint={
          <div className="space-y-1.5 text-xs leading-relaxed">
            <div>{t("detail.kpis.lossHintTitle")}</div>
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 tabular-nums">
              <span className="text-muted-foreground">
                {t("detail.kpis.lossHintInput")}
              </span>
              <span className="text-right">
                {formatNumber(engagedMT, 2)} MT
              </span>
              <span className="text-muted-foreground">
                {t("detail.kpis.lossHintOutput")}
              </span>
              <span className="text-right">
                {formatNumber(obtainedMT, 2)} MT
              </span>
              <span className="text-muted-foreground">
                {t("detail.kpis.lossHintWaste")}
              </span>
              <span className="text-right">
                {formatNumber(wasteMT, 2)} MT
              </span>
              <span className="border-t pt-0.5">
                {t("detail.kpis.lossHintLoss")}
              </span>
              <span className="border-t pt-0.5 text-right font-semibold">
                {formatNumber(ecartMT, 2)} MT
              </span>
            </div>
          </div>
        }
      />
      <KpiTile
        size="md"
        label={t("detail.kpis.lotsProduced")}
        value={
          <span className="inline-flex items-baseline gap-2">
            <span>{fullLots}</span>
            {queueLots > 0 ? (
              <span className="text-amber-700 dark:text-amber-400 text-sm font-medium tabular-nums">
                {t("detail.kpis.queueChip", { n: queueLots })}
              </span>
            ) : null}
          </span>
        }
        sub={t("detail.kpis.lotsProducedSub", { target: LOT_TARGET_BAGS })}
      />
      <KpiTile
        size="md"
        label={t("detail.kpis.completedOn")}
        value={dateFmt}
        sub={t("detail.kpis.completedSub", {
          input: formatNumber(engagedMT, 2),
        })}
      />
    </Card>
  )
}

function KpiTile({
  label,
  value,
  sub,
  hint,
  tone = "neutral",
  size = "sm",
}: {
  label: string
  value: React.ReactNode
  sub?: string
  hint?: React.ReactNode
  tone?: "ok" | "neutral"
  size?: "sm" | "md"
}) {
  const tooltipBody = hint ?? sub
  if (size === "md") {
    return (
      <div className="min-w-0 px-4 py-3.5">
        <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
          {label}
        </div>
        <div
          className={`mt-1 truncate text-2xl font-semibold tabular-nums leading-tight ${
            tone === "ok" ? "text-emerald-700 dark:text-emerald-400" : ""
          }`}
        >
          {value}
        </div>
        {sub ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-muted-foreground mt-0.5 truncate text-xs tabular-nums">
                {sub}
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" collisionPadding={8} className="max-w-xs">
              {tooltipBody}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    )
  }
  return (
    <div className="min-w-0 px-3 py-2">
      <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-0.5 flex min-w-0 items-baseline gap-2">
        <span
          className={`flex-shrink-0 text-base font-semibold tabular-nums leading-tight ${
            tone === "ok" ? "text-emerald-700 dark:text-emerald-400" : ""
          }`}
        >
          {value}
        </span>
        {sub ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground min-w-0 truncate text-[11px] tabular-nums">
                {sub}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" collisionPadding={8} className="max-w-xs">
              {tooltipBody}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </div>
  )
}

interface HelperBarProps {
  inputBags: number
  columns: CouplagePlanColumn[]
  loads: CouplingLoadView[]
  matrix: CouplageMatrix
  issues: HelperIssue[]
  canAddColumn: boolean
  onAddColumn: () => void
  balanced: boolean
  scrollRef?: React.RefObject<HTMLDivElement | null>
}

// Step 1 helper bar. Surfaces the next action plus chips for each
// blocking / informational issue.
function HelperBar({
  inputBags,
  columns,
  loads,
  matrix,
  issues,
  canAddColumn,
  onAddColumn,
  balanced,
  scrollRef,
}: HelperBarProps) {
  const { t } = useTranslation("milling")
  const [showAll, setShowAll] = useState(false)

  const plannedBags = columns.reduce(
    (a, c) => a + (Number(c.plannedBags) || 0),
    0,
  )
  const matrixBags = loads.reduce(
    (a, l) =>
      a +
      Object.values(matrix[l.loadId] ?? {}).reduce(
        (b, v) => b + (Number(v) || 0),
        0,
      ),
    0,
  )
  const unplannedBags = Math.max(0, inputBags - plannedBags)

  // Over-routed loads are the only hard error in planning. Empty
  // columns are flagged informationally — submitting an empty column
  // is allowed for drafts and the final-submit validator catches it.
  const hasBlockingIssues = issues.some((i) => i.kind === "load-overassigned")

  let title: string
  let body: string
  let action: { label: string; onClick: () => void } | null = null
  let isReady = false

  if (columns.length === 0) {
    title = t("detail.helper.noLotsTitle")
    body = t("detail.helper.noLotsBodyPlan", {
      target: LOT_TARGET_BAGS,
    })
    action = canAddColumn
      ? { label: t("detail.helper.addFirstLot"), onClick: onAddColumn }
      : null
  } else if (hasBlockingIssues) {
    title = t("detail.helper.issuesTitle", { count: issues.length })
    body = t("detail.helper.issuesBody")
    action = canAddColumn
      ? { label: t("detail.helper.addLot"), onClick: onAddColumn }
      : null
  } else if (balanced && unplannedBags > 0) {
    title = t("detail.helper.morePlanPossibleTitle", {
      bags: unplannedBags.toLocaleString(),
    })
    body = t("detail.helper.morePlanPossibleBody")
    action = canAddColumn
      ? { label: t("detail.helper.addLot"), onClick: onAddColumn }
      : null
  } else if (balanced) {
    isReady = true
    title = t("detail.helper.readyPlanTitle")
    body = t("detail.helper.readyPlanBody")
  } else {
    title = t("detail.helper.progressTitle", {
      bags: matrixBags.toLocaleString(),
      total: inputBags.toLocaleString(),
    })
    body = canAddColumn
      ? t("detail.helper.progressBody")
      : t("detail.helper.progressBodyAllAssigned")
    action = canAddColumn
      ? { label: t("detail.helper.addLot"), onClick: onAddColumn }
      : null
  }

  const tone = isReady
    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10"
    : hasBlockingIssues
      ? "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
      : "border-primary/20 bg-primary/5"

  const VISIBLE = 3
  const visibleIssues = showAll ? issues : issues.slice(0, VISIBLE)
  const hiddenCount = issues.length - visibleIssues.length

  return (
    <Card ref={scrollRef} className={`flex flex-col gap-3 p-4 ${tone}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={
              isReady
                ? "text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0"
                : hasBlockingIssues
                  ? "text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                  : "text-primary mt-0.5 flex-shrink-0"
            }
          >
            {isReady ? (
              <CheckCircle className="size-5" />
            ) : hasBlockingIssues ? (
              <AlertTriangle className="size-5" />
            ) : (
              <Info className="size-5" />
            )}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold tracking-tight">{title}</div>
            <div className="text-muted-foreground mt-0.5 text-xs">{body}</div>
          </div>
        </div>
        {action ? (
          <Button onClick={action.onClick} className="flex-shrink-0">
            <Plus className="size-4" />
            {action.label}
          </Button>
        ) : null}
      </div>

      {issues.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pl-8">
          {visibleIssues.map((issue, i) => (
            <IssueChip key={i} issue={issue} />
          ))}
          {hiddenCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="border-border hover:bg-muted text-muted-foreground hover:text-foreground inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors"
            >
              {t("detail.helper.seeAll", { n: hiddenCount })}
            </button>
          ) : null}
          {showAll && issues.length > VISIBLE ? (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="border-border hover:bg-muted text-muted-foreground hover:text-foreground inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors"
            >
              {t("detail.helper.showLess")}
            </button>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}

function IssueChip({ issue }: { issue: HelperIssue }) {
  const { t } = useTranslation("milling")
  const danger = issue.kind === "load-overassigned"
  const tone = danger
    ? "bg-destructive/10 text-destructive border-destructive/30"
    : issue.kind === "load-returning"
      ? "bg-muted/60 text-muted-foreground border-border"
      : "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"

  let text = ""
  switch (issue.kind) {
    case "empty-column":
      text = t("detail.helper.issueEmptyColumn", { label: issue.label })
      break
    case "load-overassigned":
      text = t("detail.helper.issueLoadOver", {
        label: issue.label,
        n: issue.excess,
      })
      break
    case "load-returning":
      text = t("detail.helper.issueLoadReturning", {
        label: issue.label,
        n: issue.bags,
      })
      break
  }

  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium tabular-nums ${tone}`}
    >
      {text}
    </span>
  )
}
